// ── Types ──────────────────────────────────────────────────────────────────────

export type OnboardingPersona = "SEO" | "PR" | "GENERAL";

export type OnboardingPhase =
	| "foundation"
	| "activate"
	| "insights"
	| "complete";

export interface OnboardingStep {
	key: string;
	title: string;
	tooltip: string;
	/** Path suffix relative to the property base URL (e.g. "/ai-reports") */
	path: string;
}

export interface PropertyOnboardingState {
	hasAiReports: boolean;
	discoveryQueryCount: number;
	competitorCount: number;
	pageCount: number;
	activeProjectCount: number;
	isVerified: boolean;
	hasFaqGenerations: boolean;
}

export interface OnboardingSequenceResult {
	phase: OnboardingPhase;
	persona: OnboardingPersona;
	steps: OnboardingStep[];
	state: PropertyOnboardingState;
}

// ── Step Definitions ───────────────────────────────────────────────────────────

const STEP_RUN_REPORT: OnboardingStep = {
	key: "runReport",
	title: "Run an AI Visibility Report",
	tooltip:
		"The fastest way to get started. This creates prompts and identifies competitors automatically, plus gives you a shareable report.",
	path: "/ai-reports",
};

const STEP_ADD_PROMPTS: OnboardingStep = {
	key: "addPrompts",
	title: "Set up prompts",
	tooltip:
		"Add the AI search queries you want to track. These are the prompts that will be monitored in your projects.",
	path: "/discovery-queries",
};

const STEP_ADD_COMPETITORS: OnboardingStep = {
	key: "addCompetitors",
	title: "Add competitors",
	tooltip:
		"Track competitor domains so we can calculate share of voice in AI responses.",
	path: "/competitors",
};

const STEP_IMPORT_PAGES: OnboardingStep = {
	key: "importPages",
	title: "Import your sitemap",
	tooltip:
		"Import pages from your sitemap. You can organize them into groups and use them as filters across dashboards.",
	path: "/pages",
};

const STEP_CREATE_PROJECT_SEO: OnboardingStep = {
	key: "createProject",
	title: "Set up an SEO Project",
	tooltip:
		"Projects run your prompts nightly to build statistically reliable data. Now that you have prompts set up, create a project to start tracking.",
	path: "/projects/new",
};

const STEP_CREATE_PROJECT_PR: OnboardingStep = {
	key: "createProject",
	title: "Set up a PR Project",
	tooltip:
		"Projects run your prompts nightly to build statistically reliable data. Now that you have prompts set up, create a project to start tracking.",
	path: "/projects/new",
};

const STEP_CREATE_PROJECT_GENERAL: OnboardingStep = {
	key: "createProject",
	title: "Set up a Project",
	tooltip:
		"Projects run your prompts nightly to build statistically reliable data. Now that you have prompts set up, create a project to start tracking.",
	path: "/projects/new",
};

const STEP_HISTORICAL_METRICS: OnboardingStep = {
	// Key is persisted against completed onboarding steps — never rename it.
	// The page it points at is now the dashboard's visibility drill-down.
	key: "historicalMetrics",
	title: "See your visibility trends",
	tooltip:
		"Track how your AI visibility changes over time as your project collects data.",
	path: "/dashboard/visibility",
};

const STEP_CITATION_INTELLIGENCE: OnboardingStep = {
	key: "citationIntelligence",
	title: "Explore Citation Intelligence",
	tooltip:
		"See which publishers are being cited in AI responses and track earned media coverage.",
	path: "/citation-intelligence",
};

const STEP_AI_RANKINGS: OnboardingStep = {
	key: "rankings",
	title: "Check SEO opportunities",
	tooltip:
		"See where you rank vs. competitors for each grounding search query.",
	path: "/dashboard/seo",
};

// ── Sequence Logic ─────────────────────────────────────────────────────────────

/**
 * Given the current state of a property and the user's persona, returns the
 * ordered list of recommended next steps. The in-app strip that read this
 * retired with the classic property home, so `OnboardingSequenceService`
 * is the remaining caller. Email is no longer driven from here: the setup sequence walks the setup workflow's five
 * steps instead (see `setup-sequence.ts`).
 *
 * Returns at most `maxSteps` items (default 4).
 */
export function getOnboardingSteps(
	state: PropertyOnboardingState,
	persona: OnboardingPersona,
	maxSteps = 4,
): { phase: OnboardingPhase; steps: OnboardingStep[] } {
	const items: OnboardingStep[] = [];

	const hasReport = state.hasAiReports;
	const hasPrompts = state.discoveryQueryCount > 0;
	const hasCompetitors = state.competitorCount > 0;
	const hasPages = state.pageCount > 0;
	const hasProject = state.activeProjectCount > 0;

	// Phase 1 — Foundation: get the basic data in place.
	// A Visibility Report is the fastest bootstrap (auto-creates prompts +
	// competitors), but users can also set things up manually. Every subject
	// starts there, a market included: a category report with too few prompts
	// writes its own first set rather than refusing.
	if (!hasReport) {
		items.push(STEP_RUN_REPORT);
	}
	if (!hasPrompts) {
		items.push(STEP_ADD_PROMPTS);
	}
	if (!hasCompetitors) {
		items.push(STEP_ADD_COMPETITORS);
	}
	if (!hasPages) {
		items.push(STEP_IMPORT_PAGES);
	}

	if (items.length > 0) {
		return { phase: "foundation", steps: items.slice(0, maxSteps) };
	}

	// Phase 2 — Activate: foundational data exists, create a project.
	if (!hasProject && hasPrompts) {
		const projectStep =
			persona === "PR"
				? STEP_CREATE_PROJECT_PR
				: persona === "SEO"
					? STEP_CREATE_PROJECT_SEO
					: STEP_CREATE_PROJECT_GENERAL;
		items.push(projectStep);

		return { phase: "activate", steps: items.slice(0, maxSteps) };
	}

	// Phase 3 — Insights: project is running, explore dashboards.
	if (hasProject) {
		if (persona === "PR") {
			items.push(STEP_CITATION_INTELLIGENCE, STEP_HISTORICAL_METRICS);
		} else if (persona === "SEO") {
			items.push(STEP_HISTORICAL_METRICS, STEP_AI_RANKINGS);
		} else {
			items.push(STEP_HISTORICAL_METRICS, STEP_CITATION_INTELLIGENCE);
		}

		return { phase: "insights", steps: items.slice(0, maxSteps) };
	}

	return { phase: "complete", steps: [] };
}
